import { z } from "zod";
import Connector, {
  ConnectorSecret,
  EditDefaultValuesGetter,
  IConnectorRow,
} from "./Connector";
import {
  Control,
  FieldErrors,
  FieldPath,
  UseFormRegister,
} from "react-hook-form";
import { ConnectorWizardFormValues } from "../forms/ConnectorWizard";
import { ReactNode } from "react";
import AmazonConnectConfigForm from "../forms/AmazonConnectConfigForm";

export interface AmazonConnectConnectorRow
  extends Omit<IConnectorRow, "connectorDetails"> {
  connectorDetails: {
    // Secrets (from GetSecrets)
    CTRAccessRoleARN: ConnectorSecret;
    RecordingAccessRoleARN: ConnectorSecret;
    AmazonConnectARN: ConnectorSecret;
    RecordingBucketARN: ConnectorSecret;
    CTRBucketARN: ConnectorSecret;
    RecordingDestinationBucketARN?: ConnectorSecret;
    // Plain values (from GetConnectorsEndpoint)
    CallRecordingPrefix: string;
    ChatTranscriptPrefix: string;
    ScreenRecordingPrefix: string;
    CTRBucketPrefix: string;
  };
}

// arn:aws:connect:{region}:{12-digit number}:instance/{guid}
const awsInstanceArnRegex =
  /^arn:aws:connect:\w+(?:-\w+)+:\d{12}:instance\/[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+$/;

// Specific IAM Role ARN Regex
const iamRoleArnRegex = /^arn:aws:iam::\d{12}:role\/[a-zA-Z0-9+=,.@_-]+$/;

// make sure all prefixes start with /
const prefixRegex = /^$|^\/.*/;

const InvalidPrefixFormatMessage = "amazonConnect:invalidPrefixFormat";

export const amazonConnectSchema = z.object({
  AmazonConnectARN: z
    .string({
      required_error: "amazonConnect:amazonConnectARNRequired",
    })
    .min(1, { message: "amazonConnect:amazonConnectARNEmpty" })
    .regex(awsInstanceArnRegex, "amazonConnect:amazonConnectARNBadFormat"),

  RecordingBucketName: z
    .string({
      required_error: "amazonConnect:recordingBucketNameRequired",
    })
    .min(1, { message: "amazonConnect:recordingBucketNameEmpty" }),
  RecordingBucketRegion: z
    .string({
      required_error: "amazonConnect:recordingBucketRegionRequired",
    })
    .min(1, {
      message: "amazonConnect:recordingBucketRegionEmpty",
    }),
  CTRBucketName: z
    .string({
      required_error: "amazonConnect:ctrBucketNameRequired",
    })
    .min(1, { message: "amazonConnect:ctrBucketNameEmpty" }),
  CTRBucketRegion: z
    .string({
      required_error: "amazonConnect:ctrBucketRegionRequired",
    })
    .min(1, {
      message: "amazonConnect:ctrBucketRegionEmpty",
    }),

  ShouldUseExternalBucket: z.boolean().default(false),
  RecordingDestinationBucketName: z.string().optional(),
  RecordingDestinationBucketRegion: z.string().optional(),

  CallRecordingPrefix: z
    .string()
    .regex(prefixRegex, InvalidPrefixFormatMessage),

  ChatTranscriptPrefix: z
    .string()
    .regex(prefixRegex, InvalidPrefixFormatMessage),

  ScreenRecordingPrefix: z
    .string()
    .regex(prefixRegex, InvalidPrefixFormatMessage),

  CTRBucketPrefix: z
    .string({
      required_error: "amazonConnect:ctrBucketPrefixRequired",
    })
    .min(1, { message: "amazonConnect:ctrBucketPrefixEmpty" })
    .regex(prefixRegex, InvalidPrefixFormatMessage),

  AccessRoleARN: z
    .string({
      required_error: "amazonConnect:accessRoleARNRequired",
    })
    .min(1, { message: "amazonConnect:accessRoleARNEmpty" })
    .regex(iamRoleArnRegex, "amazonConnect:accessRoleARNBadFormat"),
});

export type AmazonConnectFormValues = z.infer<typeof amazonConnectSchema>;

export default class AmazonConnectConnector
  implements Connector<AmazonConnectFormValues, AmazonConnectConnectorRow>
{
  public static readonly Type = "AmazonConnect";

  public get type(): string {
    return AmazonConnectConnector.Type;
  }

  public get defaultValues(): AmazonConnectFormValues {
    return {
      RecordingBucketRegion: "",
      CTRBucketRegion: "",
      AmazonConnectARN: "",
      RecordingBucketName: "",
      RecordingDestinationBucketName: "",
      RecordingDestinationBucketRegion: "",
      CallRecordingPrefix: "",
      ChatTranscriptPrefix: "",
      ScreenRecordingPrefix: "",
      CTRBucketName: "",
      CTRBucketPrefix: "",
      AccessRoleARN: "",
      ShouldUseExternalBucket: false,
    };
  }

  public getEditDefaultValues: EditDefaultValuesGetter<
    AmazonConnectFormValues,
    AmazonConnectConnectorRow
  > = (connector?: AmazonConnectConnectorRow): AmazonConnectFormValues => {
    const details = connector?.connectorDetails;
    return {
      // These fields are not currently returned in connectorDetails
      RecordingBucketRegion: "",
      CTRBucketRegion: "",
      RecordingBucketName: "",
      CTRBucketName: "",
      RecordingDestinationBucketName: "",
      RecordingDestinationBucketRegion: "",
      ShouldUseExternalBucket: false,
      AccessRoleARN: "",
      // Secrets from connectorDetails
      AmazonConnectARN: details?.AmazonConnectARN?.value ?? "",
      // Plain strings from connectorDetails
      CallRecordingPrefix: details?.CallRecordingPrefix ?? "",
      ChatTranscriptPrefix: details?.ChatTranscriptPrefix ?? "",
      ScreenRecordingPrefix: details?.ScreenRecordingPrefix ?? "",
      CTRBucketPrefix: details?.CTRBucketPrefix ?? "",
    };
  };

  public get fields(): FieldPath<ConnectorWizardFormValues>[] {
    return [
      ...amazonConnectSchema
        .keyof()
        .options.map((key) => `${AmazonConnectConnector.Type}.${key}`),
    ];
  }

  public renderFormFields(
    register: UseFormRegister<ConnectorWizardFormValues>,
    control: Control<ConnectorWizardFormValues>,
    errors?: FieldErrors<ConnectorWizardFormValues>
  ): ReactNode {
    return (
      <AmazonConnectConfigForm
        defaultValues={this.defaultValues}
        register={register}
        control={control}
        errors={errors}
      />
    );
  }
}
